/**
 * B2 · Hard age-gate — pure logic.
 *
 * Fail-closed throughout: bad/missing input is never "adult" or "verified".
 * The mechanism is built now; the assurance STRENGTH (self-attestation vs
 * Persona IDV vs age-estimation) is swappable via ageAssurance.ts. Raw DOB is
 * validated here but never persisted (data minimisation) — only the derived
 * `verified` state is stored.
 */

export type AgeStatus = 'unverified' | 'pending' | 'verified' | 'rejected';

/** True only if `dobISO` is a valid date ≥ 18 years before `now`. */
export const isAdultDob = (
  dobISO: string | null | undefined,
  now: Date = new Date(),
): boolean => {
  if (!dobISO) return false;
  const dob = new Date(dobISO);
  if (Number.isNaN(dob.getTime())) return false;
  if (dob.getTime() > now.getTime()) return false;
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
  return age >= 18;
};

interface AgeCheckRow {
  status?: string | null;
}

const KNOWN: ReadonlySet<AgeStatus> = new Set([
  'unverified',
  'pending',
  'verified',
  'rejected',
]);

/** Fail-closed: null / unknown status → 'unverified'. */
export const deriveAgeStatus = (row: AgeCheckRow | null | undefined): AgeStatus => {
  const raw = row?.status ?? 'unverified';
  return KNOWN.has(raw as AgeStatus) ? (raw as AgeStatus) : 'unverified';
};

/** Only a 'verified' age-check may reach gated surfaces. Fail-closed. */
export const canAccessGatedSurfaces = (status: AgeStatus): boolean =>
  status === 'verified';
